// @ts-nocheck
import React, { useContext, useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import Grid from '@material-ui/core/Grid'

import MenuItem from './MenuItem'
import Title from 'components/common/Title'
import { formatPriceAsFloat, getItemId, sanitizeId } from 'utils/utils'
import styled, { css } from 'styled-components'
import IconComponent from '../../themes/IconComponent'
import { StoreContext } from 'contexts/StoreContext'
import { useStores } from 'hooks/useStores'
import { useRouter } from 'next/router'

const SectionIcon = (props) => <IconComponent {...props} />

const StyledTitle = styled(Title)`
	color: var(--strokeMenuTitle);
	margin-top: 30px;
	@media (max-width: 576px) {
		margin-inline-start: ${({ $iconExists }) => !$iconExists && '12.5px'};
	}
`

const StyledSectionIcon = styled(SectionIcon)`
	margin-top: 30px;
	margin-inline-end: 15px;
	height: 100vh;
	width: 100vw;
	max-height: 70px;
	max-width: 70px;
	@media (max-width: 576px) {
		margin-inline-end: 10px;
		margin-inline-start: 12px;
		max-height: 45px;
		max-width: 45px;
	}
`

const StyledItemsGrid = styled(Grid)`
	background-color: var(--clear);
	display: flex;
	flex-direction: row;
	margin-bottom: 53px;
	justify-content: flex-start;
	@media (max-width: 576px) {
		justify-content: center;
		margin-bottom: 10px;
	}
	${({ $isLimitedOffers }) =>
		$isLimitedOffers &&
		css`
			max-height: 300px;
			z-index: 97;
			padding-bottom: 20px;
		`}
`

const StyledTitleGridContainer = styled(Grid)`
	padding: 0 10px 0 18px;
	margin-bottom: -3px;
	background-color: var(--clear);

	@media (max-width: 576px) {
		padding: 0 10px 0 8px;
	}
`

const StyledItemContainer = styled.div`
	display: flex;
	margin: 20px 0;
	@media (max-width: 576px) {
		width: ${({ $width }) => $width};
		height: 100%;
	}
`

const StyledRoot = styled.div`
	background-color: var(--clear);
	padding-inline-start: 22px;

	@media (max-width: 576px) {
		padding-inline-start: 0;
	}
`

const StyledItem = styled(Grid)`
	padding: 27.5px 20px;

	@media (max-width: 576px) {
		padding: 18px 7.5px 29px 7.5px;
	}
`

const TabSection = inject('Infra')(
	observer((props) => {
		const {
			isLimitedOffers,
			currency,
			items,
			locale,
			media,
			isGeneratedStatically,
			isStaticMenu,
			sectionId,
			openAllStoresAreClosedDialog,
			Infra,
			countryCode,
		} = props
		const { cartStore } = useStores()
		const sectionIconUrl = media?.logo
		const [sanitizedSectionId, setSanitizedSectionId] = useState('')
		const router = useRouter()
		const pathname = router?.asPath
		const enableMenuProductsMicroData = !isStaticMenu && Infra?.appParams?.features?.enableMenuProductsMicroData
		const conditionalProps = enableMenuProductsMicroData ? { itemScope: true, itemType: 'http://schema.org/Product' } : {}

		useEffect(() => {
			// using state for sectionId after hydration for the static menu in order to have the updated sectionId
			setSanitizedSectionId(`tab_section_${sanitizeId(sectionId)}`)
		}, [])

		return (
			<StyledRoot data-testid="section-tab">
				{!isLimitedOffers && (
					<StyledTitleGridContainer container spacing={0}>
						<Grid
							item
							xs={12}
							id={sanitizedSectionId}
							style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
							data-section-id={`section-${sanitizedSectionId}`}
						>
							{sectionIconUrl && <StyledSectionIcon assetFullUrl={sectionIconUrl} />}
							<StyledTitle variant="h2" locale={locale} $iconExists={!!sectionIconUrl} testId="section-title">
								{props.title}
							</StyledTitle>
						</Grid>
					</StyledTitleGridContainer>
				)}

				{isLimitedOffers ? (
					<StyledItemContainer $width={window.innerWidth * (items.length / 2)}>
						{props.items.map((item, index) => {
							const quantity = cartStore.items[item.id]?.length ?? 0
							return (
								<MenuItem
									key={item.id}
									item={item}
									index={index}
									sectionTitle={props.title}
									quantity={quantity}
									User={props.User}
									isGeneratedStatically={isGeneratedStatically}
									isStaticMenu={isStaticMenu}
									openAllStoresAreClosedDialog={openAllStoresAreClosedDialog}
								/>
							)
						})}
					</StyledItemContainer>
				) : (
					<StyledItemsGrid container spacing={0}>
						{props.items.map((item, index) => {
							const formattedPrice = formatPriceAsFloat(item?.price, currency, countryCode, 1, false, true)
							const itemId = getItemId(item)
							const quantity = cartStore.items[itemId]?.length ?? 0

							return (
								<StyledItem
									item
									xs={6}
									sm={6}
									md="auto"
									lg="auto"
									key={item.id}
									style={{ flexBasis: 0 }}
									data-menu-item-id={item.id}
									{...conditionalProps}
								>
									{enableMenuProductsMicroData && (
										<>
											<meta key="brand" itemProp="brand" content={Infra.appParams?.brand} />
											<meta key="name" itemProp="name" content={props?.title?.toLowerCase()} />
											<meta key="description" itemProp="description" content={item.description[locale]} />
											<meta key="productID" itemProp="productID" content={itemId} />
											<meta key="url" itemProp="url" content={`https://${location.host}${pathname}`} />
											<meta key="image" itemProp="image" content={item?.media?.logo} />

											<div key="offers" itemProp="offers" itemScope itemType="http://schema.org/Offer">
												<link key="availability" itemProp="availability" href="http://schema.org/InStock" />
												<link key="itemCondition" itemProp="itemCondition" href="http://schema.org/NewCondition" />
												<meta key="price" itemProp="price" content={`${formattedPrice}`} />
												<meta key="priceCurrency" itemProp="priceCurrency" content={currency} />
											</div>
										</>
									)}
									<MenuItem
										item={item}
										index={index}
										sectionTitle={props.title}
										quantity={quantity}
										User={props.User}
										isGeneratedStatically={isGeneratedStatically}
										isStaticMenu={isStaticMenu}
										openAllStoresAreClosedDialog={openAllStoresAreClosedDialog}
									/>
								</StyledItem>
							)
						})}
					</StyledItemsGrid>
				)}
			</StyledRoot>
		)
	})
)

export default TabSection

TabSection.propTypes = {
	title: PropTypes.string.isRequired,
	items: PropTypes.array.isRequired,
	isLimitedOffers: PropTypes.bool,
}
